import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Info, Newspaper, Plus } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  Select,
  Spinner,
  useToast,
  type SelectOption,
} from '../../ui';
import { useChanges } from '../../features/changes/useChanges';
import {
  collectEngineers,
  filterChanges,
} from '../../features/changes/changeModel';
import type {
  EngineeringChangeFilter,
  EngineeringChangeSummary,
} from '../../features/changes/types';
import { ChangeCard } from './ChangeCard';
import { ChangeModal } from './ChangeModal';

const EMPTY_FILTER: EngineeringChangeFilter = { engineer: '' };

/**
 * Change Feed — a context-first stream of engineering changes. Entries lead
 * with WHY + IMPACT (not raw volume) and are ordered by recency only; there is
 * deliberately no per-engineer count/velocity/ranking. Changes are entered
 * manually today (a human adds the context a commit log lacks); auto-derivation
 * from stories / deploys / PRs is a deferred extension point.
 */
export function ChangeFeedPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    changes,
    goals,
    workstreams,
    currentAlias,
    goalTitles,
    workstreamTitles,
    loading,
    error,
    create,
    remove,
  } = useChanges();

  const [filter, setFilter] = useState<EngineeringChangeFilter>(EMPTY_FILTER);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] =
    useState<EngineeringChangeSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const engineerOptions = useMemo<SelectOption[]>(
    () => collectEngineers(changes).map((e) => ({ value: e, label: e })),
    [changes],
  );

  const filtered = useMemo(() => filterChanges(changes, filter), [changes, filter]);
  const filtersActive = !!filter.engineer;

  const onCreate = async (draft: Parameters<typeof create>[0]) => {
    const saved = await create(draft);
    toast({ tone: 'success', title: 'Change logged', description: saved.title });
    return saved;
  };

  const onDelete = (change: EngineeringChangeSummary) => setPendingDelete(change);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await remove(pendingDelete.id);
      toast({ tone: 'success', title: 'Change removed', description: pendingDelete.title });
      setPendingDelete(null);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Could not remove change',
        description: e instanceof Error ? e.message : 'Delete failed.',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        icon={<Newspaper className="h-5 w-5" />}
        title="Change Feed"
        description="Engineering changes with the why and impact — context over volume."
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModalOpen(true)}>
            Log a change
          </Button>
        }
      />

      {/* Deferred auto-collection notice (same posture as SpecStudio). */}
      <div className="mb-5 flex items-start gap-2 rounded-xl border border-surface-border bg-surface-1/60 px-4 py-3 text-sm text-zinc-400">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" aria-hidden="true" />
        <p>
          Changes are entered manually so a human can add the{' '}
          <span className="text-zinc-200">why</span> and{' '}
          <span className="text-zinc-200">impact</span>. Auto-derivation from
          sprint stories, deploys, and PRs is planned but not available yet —
          it will pre-fill the "what changed" and links, leaving the context to
          the human.
        </p>
      </div>

      {/* Filter */}
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div className="w-48">
          <Select
            label="Engineer"
            placeholder="All engineers"
            value={filter.engineer}
            options={engineerOptions}
            onChange={(e) => setFilter({ engineer: e.target.value })}
          />
        </div>
        {filtersActive && (
          <Button variant="ghost" size="sm" onClick={() => setFilter(EMPTY_FILTER)}>
            Clear filter
          </Button>
        )}
        <span className="ml-auto self-center text-xs text-zinc-500">
          {filtered.length} of {changes.length}{' '}
          {changes.length === 1 ? 'change' : 'changes'}
        </span>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading change feed" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load the change feed"
          description={error}
        />
      ) : changes.length === 0 ? (
        <EmptyState
          icon={<Newspaper className="h-6 w-6" />}
          title="No changes logged yet"
          description="Log the first engineering change — capture the why and impact so the team stays aware."
          action={
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModalOpen(true)}>
              Log a change
            </Button>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Newspaper className="h-6 w-6" />}
          title="No changes match this filter"
          description="Try clearing the filter to see the whole feed."
        />
      ) : (
        <div className="space-y-4">
          {filtered.map((c) => (
            <ChangeCard
              key={c.id}
              change={c}
              goalTitle={c.goalId ? goalTitles.get(c.goalId) : undefined}
              workstreamTitle={
                c.workstreamId ? workstreamTitles.get(c.workstreamId) : undefined
              }
              onOpenGoal={(goalId) => navigate(`/goals/${encodeURIComponent(goalId)}`)}
              onOpenWorkstream={(wsId) =>
                navigate(`/workstreams/${encodeURIComponent(wsId)}`)
              }
              onDelete={onDelete}
            />
          ))}
        </div>
      )}

      <ChangeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        defaultEngineer={currentAlias}
        goals={goals}
        workstreams={workstreams}
        onCreate={onCreate}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title="Remove change?"
        confirmLabel="Remove change"
        loading={deleting}
        onConfirm={() => void confirmDelete()}
        onClose={() => setPendingDelete(null)}
        message={
          <>
            Remove{' '}
            <span className="font-medium text-zinc-100">{pendingDelete?.title}</span>{' '}
            from the change feed? This can't be undone.
          </>
        }
      />
    </div>
  );
}

export default ChangeFeedPage;
